import type { Move } from '@draughts/shared';
import { durataAnimazionePresa } from '@draughts/shared';
import { SOLUTION_STEP_MS } from '../../constants/timing';

/**
 * Quanto dura, a schermo, il movimento di una mossa.
 *
 * Zero per tutto ciò che non è una presa multipla: un pezzo che si sposta di una casella
 * non è animato da nessuna parte, in questa damiera — nessuna mossa lo è. Quello che si
 * legge come movimento sono i salti di una presa multipla, uno ogni `CAPTURE_STEP_MS`.
 *
 * Il conto vero sta in `durataAnimazionePresa` (`@draughts/shared`), che è la stessa
 * durata su cui il server accredita la grazia all'avversario: qui si RINOMINA soltanto,
 * perché per il replay della soluzione questa è «la durata della mossa».
 */
export function durataDellaMossa(move: Move): number {
  return durataAnimazionePresa(move);
}

/**
 * Quando mostrare ciascuna mossa della soluzione, in millisecondi dall'avvio.
 *
 * `tempi[i]` è il momento della mossa i+1. La regola è: una pausa fissa fra le mosse, PIÙ
 * il tempo che serve alla mossa precedente per arrivare in fondo.
 *
 * Quest'ultimo addendo è tutto il punto. Prima i tempi erano `600 * i` fissi, e una presa
 * da tre salti — che ha bisogno di 400 ms — veniva scavalcata dalla mossa dopo prima di
 * cominciare: compariva tutta in un fotogramma. Con la somma, invece, la mossa successiva
 * arriva quando il pezzo è arrivato davvero.
 *
 * Su una linea senza prese multiple ogni addendo è zero e la sequenza torna esattamente
 * quella di prima (600, 1200, 1800…): non è un dettaglio, è la garanzia che rimettere il
 * ritmo a posto per le prese non ha cambiato il ritmo di tutti gli altri problemi.
 */
export function tempiDellaSoluzione(moves: Move[]): number[] {
  const tempi: number[] = [];
  let t = 0;
  for (let i = 0; i < moves.length; i++) {
    // La durata che si somma è quella della mossa PRECEDENTE: è lei che deve finire.
    t += SOLUTION_STEP_MS + (i > 0 ? durataDellaMossa(moves[i - 1]!) : 0);
    tempi.push(t);
  }
  return tempi;
}
